"use strict";
// Shared Sizing Property Evaluator
// Handles width, height, min-width, min-height, max-width, max-height
// Root Type Pattern: CSSLengthValue | CSSPercentageValue | [PropertyKeywords]
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseSizingProperty = parseSizingProperty;
exports.sizingToCSSValue = sizingToCSSValue;
const shared_utils_1 = require('../utils/shared-utils.cjs');
const length_1 = require('./length.cjs');
const percentage_1 = require('./percentage.cjs');
const css_variable_1 = require('./css-variable.cjs');
/**
 * Generic sizing property parser
 * @param value - CSS value string to parse
 * @param keywords - Array of valid keywords for this specific property
 * @param requireNonNegative - Whether to enforce non-negative values (default: true)
 * @returns Parsed sizing value or null if invalid
 */
function parseSizingProperty(value, keywords, requireNonNegative = true) {
    if (!value || typeof value !== 'string') {
        return null;
    }
    const trimmed = value.trim();
    if (trimmed === '') {
        return null;
    }
    // CSS variables - parse them if valid
    if ((0, shared_utils_1.isCssVariable)(trimmed)) {
        return (0, css_variable_1.parse)(trimmed);
    }
    // Handle global keywords (inherit, initial, unset, revert, revert-layer)
    if ((0, shared_utils_1.isGlobalKeyword)(trimmed)) {
        return { type: 'keyword', keyword: trimmed.toLowerCase() };
    }
    // Handle property-specific keywords
    const lowerValue = trimmed.toLowerCase();
    if (keywords.includes(lowerValue)) {
        return { type: 'keyword', keyword: lowerValue };
    }
    // Try to parse as percentage first (before length, since length includes %)
    const percentageResult = (0, percentage_1.parse)(trimmed);
    if (percentageResult) {
        if (requireNonNegative && !(0, shared_utils_1.isNonNegative)(percentageResult)) {
            return null;
        }
        return percentageResult;
    }
    // Try to parse as length
    const lengthResult = (0, length_1.parse)(trimmed);
    if (lengthResult) {
        if (requireNonNegative && !(0, shared_utils_1.isNonNegative)(lengthResult)) {
            return null;
        }
        return lengthResult;
    }
    return null;
}
/**
 * Generic sizing property toCSSValue converter
 * @param parsed - Parsed sizing value
 * @returns CSS string or null if invalid
 */
function sizingToCSSValue(parsed) {
    if (!parsed) {
        return null;
    }
    // Handle CSS variables - use the proper CSS variable serializer
    if ('CSSvariable' in parsed) {
        return (0, css_variable_1.toCSSValue)(parsed);
    }
    // Handle keywords
    if ('keyword' in parsed) {
        return parsed.keyword;
    }
    // Handle length values (including calc expressions)
    if ('unit' in parsed) {
        return (0, length_1.toCSSValue)(parsed);
    }
    // Handle percentage values (including calc expressions)
    if ('value' in parsed && !('unit' in parsed)) {
        return (0, percentage_1.toCSSValue)(parsed);
    }
    // Handle calc expressions that could be length or percentage
    if ('expression' in parsed) {
        return (0, length_1.toCSSValue)(parsed) || (0, percentage_1.toCSSValue)(parsed);
    }
    return null;
}
